import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Bars3Icon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useClientsQuery } from '@/features/Clients/Data';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import { useEstadiasClienteQuery, useEquipamentosQuery } from '../../../hooks/data';
import {
  aplicarSelecaoEstadias,
  contarEstadias,
  estadiaCorrespondeTexto,
  STATUS_ESTADIA_PADRAO,
  type ContextoBuscaEstadia,
  type SelecaoEstadias,
} from '../estadiasCliente.utils';
import EstadiaClienteCard from './EstadiaClienteCard';
import EnviarParaClienteDrawer from './EnviarParaClienteDrawer';
import EstadiasResumoPainel from './EstadiasResumoPainel';
import EstadiasKanban, { type AgrupamentoKanbanEstadias } from './EstadiasKanban';

type ModoVisualizacaoEstadias = 'lista' | 'kanban';

interface PreferenciasEstadias extends SelecaoEstadias {
  modo: ModoVisualizacaoEstadias;
  agrupamento: AgrupamentoKanbanEstadias;
}

const chavePreferencias = (uid: string | undefined) => `si:estadiasClienteFiltro:${uid ?? 'anon'}`;

/** Mesmo padrão de lerFiltroPersistido (SolicitacoesListPage): lembra filtro/visualização
 * por pessoa; falha silenciosamente sem localStorage. */
function lerPreferencias(uid: string | undefined): Partial<PreferenciasEstadias> {
  try {
    const bruto = localStorage.getItem(chavePreferencias(uid));
    return bruto ? JSON.parse(bruto) : {};
  } catch {
    return {};
  }
}

/**
 * Fase 1 do fluxo de máquina homologada por Cliente — preparação/estoque de equipamento
 * ANTES de existir Solicitação (ver estadiaCliente.types.ts). Mesmo formato do Controle
 * de Solicitações: busca livre + painel de resumo (que também filtra) + Lista ou Kanban
 * (Por Status / Por Cliente). Busca todas as estadias de uma vez e filtra no client — o
 * volume é pequeno e é isso que permite as contagens por opção.
 */
export default function EstadiasClienteSection() {
  const { user } = useAuth();
  const preferencias = useMemo(() => lerPreferencias(user?.uid), [user?.uid]);

  const [selecao, setSelecao] = useState<SelecaoEstadias>({
    status: preferencias.status ?? STATUS_ESTADIA_PADRAO,
    clientes: preferencias.clientes ?? [],
    motivos: preferencias.motivos ?? [],
  });
  const [modo, setModo] = useState<ModoVisualizacaoEstadias>(preferencias.modo ?? 'lista');
  const [agrupamento, setAgrupamento] = useState<AgrupamentoKanbanEstadias>(preferencias.agrupamento ?? 'status');
  // Busca não persiste de propósito (mesmo motivo das Solicitações: não abrir a tela vazia sem explicação).
  const [busca, setBusca] = useState('');
  const [enviarAberto, setEnviarAberto] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(chavePreferencias(user?.uid), JSON.stringify({ ...selecao, modo, agrupamento }));
    } catch {
      // não é crítico — só não persiste.
    }
  }, [user?.uid, selecao, modo, agrupamento]);

  const { data: estadias = [], isLoading } = useEstadiasClienteQuery();
  const { data: equipamentos = [] } = useEquipamentosQuery();
  const { data: clientes = [] } = useClientsQuery();
  const { data: colaboradores = [] } = useCollaboratorsPublicQuery();

  const contexto = useMemo<ContextoBuscaEstadia>(
    () => ({
      equipamentoPorId: new Map(equipamentos.map((equipamento) => [equipamento.id, equipamento])),
      nomeClientePorId: new Map(clientes.map((cliente) => [cliente.id, cliente.name])),
      nomeColaboradorPorId: new Map(colaboradores.map((colaborador) => [colaborador.id, colaborador.name])),
    }),
    [equipamentos, clientes, colaboradores]
  );

  const base = useMemo(
    () => estadias.filter((estadia) => estadiaCorrespondeTexto(estadia, busca, contexto)),
    [estadias, busca, contexto]
  );
  const exibidas = useMemo(() => aplicarSelecaoEstadias(base, selecao), [base, selecao]);
  const contagens = useMemo(() => contarEstadias(base, selecao), [base, selecao]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          className="input-primary w-full max-w-sm"
          placeholder="Pesquise aqui... (patrimônio, modelo, série, cliente, cotado)"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
        />
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-md border border-gray-200 p-0.5">
            {([
              { valor: 'lista', label: 'Lista', Icon: Bars3Icon },
              { valor: 'kanban', label: 'Kanban', Icon: Squares2X2Icon },
            ] as const).map(({ valor, label, Icon }) => (
              <button
                key={valor}
                type="button"
                onClick={() => setModo(valor)}
                className={clsx(
                  'flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium',
                  modo === valor ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
          <button type="button" className="btn-primary" onClick={() => setEnviarAberto(true)}>
            Enviar máquina(s) para Cliente
          </button>
        </div>
      </div>

      {!isLoading && (
        <EstadiasResumoPainel
          contagens={contagens}
          selecao={selecao}
          onChange={(parcial) => setSelecao((atual) => ({ ...atual, ...parcial }))}
          clientes={clientes}
        />
      )}

      {isLoading && <p className="text-sm text-gray-500">Carregando...</p>}
      {!isLoading && exibidas.length === 0 && (
        <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Nenhuma estadia encontrada.
        </p>
      )}

      {exibidas.length > 0 && modo === 'kanban' && (
        <EstadiasKanban
          estadias={exibidas}
          statusSelecionados={selecao.status}
          contexto={contexto}
          agrupamento={agrupamento}
          onChangeAgrupamento={setAgrupamento}
        />
      )}

      {exibidas.length > 0 && modo === 'lista' && (
        <div className="space-y-2">
          {exibidas.map((estadia) => {
            const equipamento = contexto.equipamentoPorId.get(estadia.equipamentoId);
            return (
              <EstadiaClienteCard
                key={estadia.id}
                estadia={estadia}
                patrimonioEquipamento={equipamento?.patrimonio ?? null}
                modeloEquipamento={equipamento?.modelo ?? null}
                nSerieEquipamento={equipamento?.nSerie ?? null}
                nomeCliente={contexto.nomeClientePorId.get(estadia.clienteId) ?? null}
                nomeColaboradorCotado={
                  estadia.colaboradorCotadoId
                    ? (contexto.nomeColaboradorPorId.get(estadia.colaboradorCotadoId) ?? null)
                    : null
                }
              />
            );
          })}
        </div>
      )}

      <EnviarParaClienteDrawer isOpen={enviarAberto} onClose={() => setEnviarAberto(false)} />
    </div>
  );
}
